import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { updateDesignByPrompt } from 'api/updateDesignByPrompt';
import { ReactComponent as BackIcon } from 'assets/icons/chevron_left.svg';
import cardGeneratorImg from 'assets/images/design_ai_card_generator.png';
import { DESIGN_GENERATED } from 'constants/mixpanel';
import { saveGeneratedDesign } from 'libs/e1api/infographic';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { RootState, store } from 'modules/Editor/store';
import { selectInfograph } from 'modules/Editor/store/infographSelector';
import { LoadingButton } from 'modules/common/components/Button/LoadingButton';
import { useToast } from 'modules/common/components/Toast';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReplaceWidgetInfoType } from 'types/ai.types';
import { InfographState } from 'types/infographTypes';

import { DesignGenerationType } from '../DesignGenerator.types';
import { DesignPreview } from '../components/DesignPreview/DesignPreview';
import { processCardTemplate, randomlySelectBaseTemplate } from './CardGenerator.helpers';
import { PromptForm } from './PromptForm';

interface CardGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CardGeneratorModal = ({ isOpen, onClose }: CardGeneratorModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='4xl' trapFocus={false}>
      <ModalOverlay />
      <CardGeneratorModalContent onClickBack={onClose} />
    </Modal>
  );
};

interface CardGeneratorModalContentProps {
  onClickBack: () => void;
}

const CardGeneratorModalContent = ({ onClickBack }: CardGeneratorModalContentProps) => {
  const { t } = useTranslation('design_generator', { keyPrefix: 'cardGeneration' });

  const toast = useToast();

  // Store the source template id for the generated design for tracking purposes
  const e1SourceTemplateId = useRef('');
  const [generatedInfograph, setGeneratedInfograph] = useState<InfographState | null>(null);

  // Button loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const showError = useCallback(
    (message: string) =>
      toast({
        title: message,
        status: 'error',
      }),
    [toast],
  );

  /**
   * Triggered when clicking on generate button
   * Randomly selects a card template and updates based on user inputs
   */
  const onClickGenerate = useCallback(
    async (cardType: string, cardFor: string) => {
      if (!cardType || !cardFor) return;

      setIsGenerating(true);

      try {
        const baseTemplate = randomlySelectBaseTemplate(generatedInfograph?.id);
        let replaceWidgetInfo = undefined;
        if (baseTemplate.replaceables?.icons) {
          replaceWidgetInfo = { type: 'icon', num: baseTemplate.replaceables.icons.length };
        } else if (baseTemplate.replaceables?.images) {
          replaceWidgetInfo = { type: 'image', num: baseTemplate.replaceables.images.length };
        }
        const { status, updatedInfographState, searchKeywords } = await updateDesignByPrompt({
          designId: baseTemplate.e2DesignId,
          prompt: `${cardType} for ${cardFor}`,
          type: DesignGenerationType.Card,
          replaceWidgetInfo: replaceWidgetInfo as ReplaceWidgetInfoType | undefined,
        });

        if (status !== 'ok') {
          throw new Error('Failed to update design by prompt');
        }

        const processedInfographState = await processCardTemplate({
          baseTemplateData: baseTemplate,
          generatedInfograph: updatedInfographState,
          keywords: searchKeywords,
        });
        setGeneratedInfograph(processedInfographState);
        e1SourceTemplateId.current = baseTemplate.e1TemplateId;

        Mixpanel.track(DESIGN_GENERATED.eventName, {
          template_id: baseTemplate.e2DesignId,
          template_category: DESIGN_GENERATED.templateCategoryName.Cards,
          kind_input: cardType,
          for_input: cardFor,
        });
      } catch (err) {
        console.error(err);
        showError('Failed to generate design. Please try again in a few moments.');
      } finally {
        setIsGenerating(false);
      }
    },
    [showError, generatedInfograph],
  );

  /**
   * Triggered when clicking on edit button
   * Will duplicate the design and open the editor
   */
  const onClickEdit = async () => {
    if (!generatedInfograph) return;

    // Save design to user's account
    try {
      setIsSaving(true);
      const storeState = store.getState() as RootState;
      const response = await saveGeneratedDesign(selectInfograph(storeState), e1SourceTemplateId.current);

      // Free users have limit of 5 designs
      if (response.status === 'overlimit') {
        showError(
          'You have reached the maximum number of designs on your account. Please delete some designs and try again.',
        );
        return;
      }

      if (response.status === 'ok' && response.designId) {
        window.location.href = `/design/${response.designId}`;
      } else {
        throw new Error('Failed to save design. Please try again.');
      }
    } catch (err) {
      console.error(err);
      showError('Failed to save design. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalContent>
      <ModalHeader>
        <Button
          onClick={onClickBack}
          leftIcon={<BackIcon style={{ marginRight: '0.5rem' }} />}
          size='xs'
          variant='ghost'
        >
          {t('backButton')}
        </Button>
      </ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        <Flex justifyContent='space-between' gap={4} pb={4}>
          <Flex direction='column' gap={4} w='50%'>
            <Text fontWeight='semibold' fontSize='md'>
              {t('heading')}
            </Text>
            <PromptForm
              onSubmit={onClickGenerate}
              isLoading={isGenerating}
              generateBtnLabel={!!generatedInfograph ? t('generateAgainBtn') : t('generateButton')}
            />
          </Flex>

          {/* Initial thumbnail image */}
          {!generatedInfograph && (
            <Box flexGrow={1}>
              <Image src={cardGeneratorImg} objectFit='contain' alt={t('altText')} />
            </Box>
          )}

          {/* Generated infograph preview */}
          {generatedInfograph && (
            <VStack w='50%'>
              <DesignPreview infographState={generatedInfograph} />
              <LoadingButton
                variant='action'
                size='sm'
                onClick={onClickEdit}
                isLoading={isSaving}
                loadingText={t('editBtnLoading')}
              >
                {t('editButton')}
              </LoadingButton>
            </VStack>
          )}
        </Flex>
      </ModalBody>
    </ModalContent>
  );
};
